import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Send,
  BookOpen,
  Award,
  Clock,
  FileText,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const QuizPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const quizId = searchParams.get("id");
  
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      // Get quiz
      const url = quizId ? `${API}/quiz/${quizId}` : `${API}/quiz/default`;
      const response = await axios.get(url);
      
      if (!response.data) {
        toast.error("Test non trovato");
        return;
      }
      
      setQuiz(response.data);
      
      // Check if already completed
      const statusResponse = await axios.get(`${API}/my-quiz-status/${response.data.quiz_id}`);
      if (statusResponse.data.completed) {
        setAlreadyCompleted(true);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Devi effettuare il login per compilare il test");
        navigate("/login");
      } else {
        toast.error("Errore nel caricamento del test");
      }
    } finally {
      setLoading(false);
    }
  };

  // Group questions by section
  const getSections = () => {
    if (!quiz) return [];
    const sections = [];
    let currentSectionQuestions = [];
    let currentSectionName = "";
    
    quiz.questions.forEach((q, index) => {
      if (q.section !== currentSectionName) {
        if (currentSectionQuestions.length > 0) {
          sections.push({ name: currentSectionName, questions: currentSectionQuestions });
        }
        currentSectionName = q.section || "Domande";
        currentSectionQuestions = [q];
      } else {
        currentSectionQuestions.push(q);
      }
    });
    
    if (currentSectionQuestions.length > 0) {
      sections.push({ name: currentSectionName, questions: currentSectionQuestions });
    }
    
    return sections;
  };

  const sections = getSections();
  
  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const getProgress = () => {
    if (!quiz) return 0;
    const answered = Object.keys(answers).length;
    return Math.round((answered / quiz.questions.length) * 100);
  };

  const isCurrentSectionComplete = () => {
    if (sections.length === 0) return true;
    const sectionQuestions = sections[currentSection]?.questions || [];
    return sectionQuestions.every(q => answers[q.question_id]);
  };

  const handleSubmit = async () => {
    // Check all questions answered
    const unanswered = quiz.questions.filter(q => !answers[q.question_id]);
    if (unanswered.length > 0) {
      toast.error(`Hai ${unanswered.length} domande senza risposta. Completa tutte le domande prima di inviare.`);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/quiz/${quiz.quiz_id}/submit`, { answers });
      setCompleted(true);
      toast.success("Test inviato con successo! 🎉");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio del test");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (alreadyCompleted) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="rounded-3xl border-border/50 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-8 text-white text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Test già completato!</h1>
              <p className="opacity-90">Hai già inviato le tue risposte a questo test.</p>
            </div>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground mb-6">
                Le tue risposte sono state inviate alla docente. Grazie per aver completato il questionario!
              </p>
              <Button onClick={() => navigate("/")} className="rounded-full">
                Torna alla Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  if (completed) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="rounded-3xl border-border/50 overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-pink-500 p-8 text-white text-center">
              <Award className="w-16 h-16 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Test Completato! 🎉</h1>
              <p className="opacity-90">Le tue risposte sono state inviate alla docente.</p>
            </div>
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Grazie per aver completato il test!</h2>
              <p className="text-muted-foreground mb-6">
                La docente riceverà le tue risposte via email e potrà valutare il tuo livello di preparazione prima del corso.
              </p>
              <Button onClick={() => navigate("/")} className="rounded-full">
                Torna alla Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  if (!quiz) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Test non disponibile</h1>
          <p className="text-muted-foreground mb-6">Il test richiesto non è stato trovato.</p>
          <Button onClick={() => navigate("/")} className="rounded-full">
            Torna alla Home
          </Button>
        </div>
      </PageLayout>
    );
  }

  const currentSectionData = sections[currentSection];

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-semibold">{quiz.title}</h1>
              <p className="text-muted-foreground text-sm">{quiz.description}</p>
            </div>
          </div>
          
          {/* Progress */}
          <Card className="rounded-2xl border-border/50 bg-gradient-to-r from-primary/5 to-pink-500/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Progresso</span>
                <span className="text-sm text-muted-foreground">
                  {Object.keys(answers).length}/{quiz.questions.length} risposte
                </span>
              </div>
              <Progress value={getProgress()} className="h-2" />
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  ~15 minuti
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {sections.length} sezioni
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {sections.map((section, index) => {
            const sectionComplete = section.questions.every(q => answers[q.question_id]);
            return (
              <Button
                key={index}
                variant={currentSection === index ? "default" : "outline"}
                size="sm"
                className={`rounded-full whitespace-nowrap ${
                  sectionComplete ? "border-green-500" : ""
                }`}
                onClick={() => setCurrentSection(index)}
              >
                {sectionComplete && <CheckCircle className="w-3 h-3 mr-1 text-green-500" />}
                {section.name.replace(/Sezione \d+ – /, "").substring(0, 20)}
                {section.name.length > 20 && "..."}
              </Button>
            );
          })}
        </div>

        {/* Current Section */}
        <Card className="rounded-3xl border-border/50 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl text-primary">{currentSectionData?.name}</CardTitle>
            <CardDescription>
              {currentSectionData?.questions.filter(q => answers[q.question_id]).length}/
              {currentSectionData?.questions.length} domande completate
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentSectionData?.questions.map((question, qIndex) => (
              <div
                key={question.question_id}
                className={`p-5 rounded-2xl transition-all ${
                  answers[question.question_id]
                    ? "bg-green-50 border-2 border-green-200"
                    : "bg-secondary/50 border-2 border-transparent"
                }`}
                data-testid={`question-${question.question_id}`}
              >
                <p className="font-medium mb-4 text-foreground">{question.question_text}</p>
                
                {question.question_type === "multiple_choice" ? (
                  <div className="space-y-2">
                    {question.options?.map((option, oIndex) => {
                      const optionLetter = option.charAt(0);
                      const isSelected = answers[question.question_id] === optionLetter;
                      return (
                        <button
                          key={oIndex}
                          onClick={() => handleAnswerChange(question.question_id, optionLetter)}
                          className={`w-full text-left p-4 rounded-xl transition-all ${
                            isSelected
                              ? "bg-primary text-white shadow-lg shadow-primary/25"
                              : "bg-background hover:bg-primary/5 border border-border"
                          }`}
                          data-testid={`option-${question.question_id}-${optionLetter}`}
                        >
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full mr-3 text-sm font-bold ${
                            isSelected ? "bg-white/20" : "bg-primary/10 text-primary"
                          }`}>
                            {optionLetter}
                          </span>
                          {option.substring(3)}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <Textarea
                    placeholder="Scrivi qui la tua risposta..."
                    value={answers[question.question_id] || ""}
                    onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                    className="min-h-[120px] rounded-xl"
                    data-testid={`textarea-${question.question_id}`}
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            className="rounded-full"
            disabled={currentSection === 0}
            onClick={() => setCurrentSection(prev => prev - 1)}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Sezione precedente
          </Button>
          
          {currentSection < sections.length - 1 ? (
            <Button
              className="rounded-full"
              onClick={() => setCurrentSection(prev => prev + 1)}
            >
              Sezione successiva
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              className="rounded-full bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90"
              onClick={handleSubmit}
              disabled={submitting || getProgress() < 100}
            >
              {submitting ? (
                <>Invio in corso...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia Test
                </>
              )}
            </Button>
          )}
        </div>

        {/* Submit reminder */}
        {getProgress() < 100 && currentSection === sections.length - 1 && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            Completa tutte le domande per poter inviare il test
          </p>
        )}
      </div>
    </PageLayout>
  );
};

export default QuizPage;
